import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type {
    SpatialLevelResumeDto,
    SpatialLevelsResumeDto,
} from "@station/data/dto/spatial-level-resume.dto";
import {
    SpatialLevelResumeDtoSchema,
    SpatialLevelsResumeDtoSchema,
} from "@station/data/dto/spatial-level-resume.dto";
import {
    SpatialLevelResume,
    SpatialLevelsResume,
} from "@station/domain/models/spatial-level-resume.model";
import { injectable } from "inversify";
import type { Result } from "neverthrow";
import { err } from "neverthrow";

@injectable()
export class SpatialLevelResumeMapper {
    map(
        spatialLevelResumeDto: SpatialLevelResumeDto,
    ): Result<SpatialLevelResume, ModelError<typeof SpatialLevelResume>> {
        const spatialLevelResumeDtoParsed =
            SpatialLevelResumeDtoSchema.safeParse(spatialLevelResumeDto);

        if (!spatialLevelResumeDtoParsed.success) {
            console.error(spatialLevelResumeDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return SpatialLevelResume.createWithRecovery({
            id: spatialLevelResumeDto.id,
            name: spatialLevelResumeDto.name,
            toLabel: spatialLevelResumeDto.to_label,
            users: spatialLevelResumeDto.users.map((user) => ({
                id: user.id,
                email: user.email,
            })),
        });
    }

    mapList(
        spatialLevelResumesDto: SpatialLevelsResumeDto,
    ): SpatialLevelsResume {
        const spatialLevelResumesDtoParsed =
            SpatialLevelsResumeDtoSchema.safeParse(spatialLevelResumesDto);

        if (!spatialLevelResumesDtoParsed.success) {
            console.error(spatialLevelResumesDtoParsed.error.issues);
            return SpatialLevelsResume.empty();
        }

        const spatialLevelsResume = spatialLevelResumesDto.mapNotNull(
            (spatialLevelResumeDto) =>
                this.map(spatialLevelResumeDto).match(
                    (spatialLevelResume) => spatialLevelResume,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping spatial level:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
        );

        return SpatialLevelsResume.create({
            spatialLevelsResume,
            count: spatialLevelsResume.length,
        }).unwrapOr(SpatialLevelsResume.empty());
    }
}
